/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import MobiusLink from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

/* Husky Custom Code - Start */
import translate from "@insite/client-framework/Translate";
import { ResolveThunks } from "react-redux";
/* Husky Custom Code - End */

const enum fields {
    destination = "destination",
    overrideTitle = "overrideTitle",
    customCSS = "customCSS",
    // Husky Custom Code
    linkColor = "linkColor",
    fontSize = "fontSize",
    fontWeight = "fontWeight",
    letterSpacing = "letterSpacing",
    paddingTop = "paddingTop",
    marginBottom = "marginBottom",
    textCapitalize = "textCapitalize",
    openInNewWindow = "openInNewWindow",
    showExternalLinkIcon = "showExternalLinkIcon",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.destination]: LinkFieldValue;
        [fields.overrideTitle]: string;
        [fields.customCSS]: string;
        // Husky Custom Code
        [fields.linkColor]: string;
        [fields.fontSize]: number;
        [fields.fontWeight]: number;
        [fields.letterSpacing]: number;
        [fields.paddingTop]: number;
        [fields.marginBottom]: number;
        [fields.textCapitalize]: boolean;
        [fields.openInNewWindow]: boolean;
        [fields.showExternalLinkIcon]: boolean;
    };
}

const linkStyles: InjectableCss = {};

export const Link: FC<OwnProps> = ({ fields }) => {
    const { title, url } = useGetLink(fields.destination);

    /* Husky Custom Code - Start */
    const styles = css`
        font-size: ${fields.fontSize}px;
        font-weight: ${fields.fontWeight};
        letter-spacing: ${fields.letterSpacing}px;
        padding-top: ${fields.paddingTop}px;
        margin-bottom: ${fields.marginBottom}px;
        text-transform: ${fields.textCapitalize ? "uppercase" : "initial"};
    `;
    /* Husky Custom Code - End */

    if (!url) {
        return null;
    }

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            {/* Husky Custom Code - Start */}
            <MobiusLink
                className="link"
                href={url}
                css={styles}
                color={fields.linkColor}
                target={fields.openInNewWindow ? "_blank" : ""}
                icon={{
                    iconProps: {
                        size: 12,
                        color: "danger.main",
                        css: css`
                            margin: 5px;
                        `,
                        src: fields.showExternalLinkIcon ? "ExternalLink" : undefined,
                    },
                    position: "right",
                }}
            >
                {translate(fields.overrideTitle || title || url)}
            </MobiusLink>
            {/* Husky Custom Code - End */}
        </StyledWrapper>
    );
};

const defaultCustomCss = `.link {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: Link,
    definition: {
        group: "Basic",
        icon: "link",
        fieldDefinitions: [
            {
                name: fields.destination,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "Contextual",
                isRequired: true,
                tab: basicTab,
            },
            {
                name: fields.overrideTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                tab: basicTab,
            },
            /* Husky Custom Code - Start */
            {
                fieldType: "General",
                name: fields.linkColor,
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                defaultValue: "black",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.fontSize,
                editorTemplate: "IntegerField",
                displayName: "Font Size",
                defaultValue: 13,
                max: 100,
                min: 0,
                isRequired: true,
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.fontWeight,
                editorTemplate: "IntegerField",
                displayName: "Font Weight",
                defaultValue: 900,
                max: 1000,
                min: 0,
                isRequired: true,
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.letterSpacing,
                editorTemplate: "IntegerField",
                displayName: "Letter Spacing",
                defaultValue: 0,
                max: 1000,
                min: 0,
                isRequired: true,
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.paddingTop,
                editorTemplate: "IntegerField",
                displayName: "Padding Top",
                defaultValue: 0,
                max: 1000,
                min: 0,
                isRequired: true,
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.marginBottom,
                editorTemplate: "IntegerField",
                displayName: "Margin Bottom",
                defaultValue: 0,
                max: 1000,
                min: 0,
                isRequired: true,
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.textCapitalize,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            {
                name: "openInNewWindow",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                tab: basicTab,
            },
            {
                name: "showExternalLinkIcon",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                isVisible: widget => widget.fields.openInNewWindow,
                tab: basicTab,
            },
            /* Husky Custom Code - End */
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
